import { CommonModule } from '@angular/common';
import {
  Component, computed, OnChanges, OnDestroy,
  OnInit, SimpleChanges, ViewEncapsulation, ViewChild
} from '@angular/core';

import { DrawingComponent } from '@shared/drawing/drawing.component';
import { ViewerAssetType } from '@shared/enums/viewer-asset';
import { Orientation } from '@shared/viewer/enums/orientation';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { ViewerHelpers } from '../viewer-helpers';
import { ViewerBaseComponent } from '../viewer-shared/viewer-base.component';
import { ViewerService } from '../viewer.service';

@Component({
  selector: 'zbp-viewer-canvas',
  templateUrl: './viewer-canvas.component.html',
  styleUrls: ['./viewer-canvas.component.scss',
    '../viewer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, DrawingComponent]
})
export class ViewerCanvasComponent extends ViewerBaseComponent implements OnChanges, OnInit, OnDestroy {
  readonly backgroundAssetTypes: ViewerAssetType[] = [
    ViewerAssetType.Canvas,
    ViewerAssetType.Image,
    ViewerAssetType.ImagePrint,
  ];
  readonly landscapeGrades: string[] = ['pk', 'k', '1', '2'];
  background: HTMLImageElement = null;
  subscriptions: Subscription[] = [];
  // Add ViewChild for the DrawingComponent to be able to reset digital ink
  @ViewChild(DrawingComponent) drawing: DrawingComponent;

  constructor(
    public viewerService: ViewerService,
  ) {
    super();
  }

  assetChanged$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  readonly shouldAnnounce = computed(() => {
    const type = this.asset?.viewerAssetType;
    return type === ViewerAssetType.Image || type === ViewerAssetType.ImagePrint;
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.asset) {
      // Reset digital ink when asset changes
      if (this.drawing) {
        this.drawing.clear();
      }
      this.loadImage();
      this.assetChanged$.next(true);
    }
  }

  loadImage(): void {
    this.viewerService.loadImage(this.getBackground())
      .pipe(
        catchError((err) => {
          this.withError.emit(err);
          return of(null);
        })
      )
      .subscribe((el: HTMLImageElement) => {
        if (el) {
          this.background = el;
          this.zoomLevel = 100;
        }
        // Get out of "loading" state regardless.
        this.loaded.emit();
      });
  }

  background$: Observable<string> = this.assetChanged$.pipe(
    filter(assetChanged => !!assetChanged),
    map(() => this.getBackground())
  );

  getBackground(): string {
    if (this.backgroundAssetTypes.includes(this.asset?.viewerAssetType)) {
      return this.viewerService.getAssetFilenameUrl(this.asset, 'image', this.asset.language);
    }

    // Special handwriting background.
    return `/assets/img/handwriting_models_lines.png`;
  }

  /**
   * Gets a layout orientation based on the asset image or grade.
   */
  getOrientation(): Orientation {
    if (this.asset?.viewerAssetType === ViewerAssetType.Canvas
      || this.asset?.viewerAssetType === ViewerAssetType.Image
      || this.asset?.viewerAssetType === ViewerAssetType.ImagePrint) {
      if (this.background) {
        const aspect = this.background.naturalWidth / this.background.naturalHeight;
        // Aspect ratios are not exact because legacy Superkids has inconsistent/non-standard images.
        if (aspect > 1.1) {
          return Orientation.Landscape;
        }
        if (aspect < .9 && (aspect === (1080 / 1920)
          || !ViewerHelpers
            .isSuperkidsProduct(ViewerHelpers.getProductTypeByComponentId(this.viewerService.componentId)))) {
          return Orientation.Portrait;
        }
        if (aspect !== (1080 / 1920)
          && ViewerHelpers
            .isSuperkidsProduct(ViewerHelpers.getProductTypeByComponentId(this.viewerService.componentId))) {
          return Orientation.NarrowPortrait;
        }

        return Orientation.NonStandard;
      }

      if (this.landscapeGrades.includes(this.grade)) {
        return Orientation.Landscape;
      }
      return Orientation.Portrait;
    }

    // VideoCanvas has set width/height.
    return Orientation.None;
  }

  getWrapperClasses(): any {
    const orientation = this.getOrientation();
    return {
      // Ignore scaled-(vw-)centered styles when zoomed in and set 100% width, which will then be auto-adjusted by the
      // drawing.component when resize is done.
      viewerCanvasWrapperPortrait: orientation === Orientation.Portrait && this.zoomLevel >= 100,
      viewerCanvasWrapperLandscape: orientation === Orientation.Landscape && this.zoomLevel >= 100,
      viewerCanvasWrapperNonStandard: orientation === Orientation.NonStandard && this.zoomLevel >= 100,
      viewerCanvasWrapperSuperKidsNarrowPortrait: orientation === Orientation.NarrowPortrait && this.zoomLevel >= 100,
      viewerCanvasWrapperLines: orientation === Orientation.None && this.zoomLevel >= 100,
    };
  }

  ngOnInit(): void {
    this.subscriptions.push(this.background$.subscribe());
    this.assetChanged$.next(true);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
